import { nanoid } from 'nanoid';
import { StoreSlice } from '@/store';
import {
  EvaluationErrorType,
  EvaluationValueKind,
  GraphRefType,
  NodeEvaluationStatus,
  buildDag,
} from '../../types';
import {
  GraphBatchClaim,
  GraphBatchExecutionOutcome,
  GraphBatchGateway,
} from './contracts';
import { NodeEvalStatus } from './types';
import { match } from 'ts-pattern';
import { TERMINAL_NODE_ID } from '../../config';
import { toEvaluationValue } from '../../utils';

type GlobalStoreApi = {
  getState: () => StoreSlice;
  subscribe: (
    listener: (state: StoreSlice, prevState?: StoreSlice) => void
  ) => () => void;
};

const signature = (state: StoreSlice) =>
  `${state.graphs.batchQueue.length}:${
    state.graphs.batchInFlight?.id ?? 'none'
  }`;

export const createGraphBatchGateway = (
  store: GlobalStoreApi
): GraphBatchGateway => {
  const leaseByBatchId = new Map<string, string>();
  const graphStore = () => store.getState().graphs;
  const entryStore = () => store.getState().entries;

  return {
    resetStateMachine: () => {
      graphStore().resetBatchSM();
    },
    subscribeToSignals: (listener) => {
      let current = signature(store.getState());

      return store.subscribe((state) => {
        const next = signature(state);
        if (next === current) return;
        current = next;
        listener();
      });
    },
    hasClaimableWork: () => {
      const s = graphStore();
      return s.batchQueue.length > 0 && s.batchInFlight === null;
    },
    claimNextBatch: () => {
      const res = graphStore().claimBatch();
      if (res.isErr()) return null;

      const batch = res.value;
      const leaseId = nanoid();

      leaseByBatchId.set(batch.id, leaseId);

      return { leaseId, batch };
    },
    finishClaim: (
      claim: GraphBatchClaim,
      completion: GraphBatchExecutionOutcome
    ) => {
      const lease = leaseByBatchId.get(claim.batch.id);
      if (!lease) return;
      if (lease !== claim.leaseId) return;

      leaseByBatchId.delete(claim.batch.id);
      graphStore().finishBatch(claim.batch.id);

      if (completion.kind === 'failed') {
        const s = graphStore();
        const e = entryStore();
        match(s.get(claim.batch.graphId).ref)
          .with({ kind: GraphRefType.Field }, (fieldRef) => {
            e.setComputed(fieldRef.fieldId, null);
          })
          .otherwise(() => void 0);
      }
    },
    getGraphSnapshot: (graphId) => {
      const s = graphStore();
      const graph = s.get(graphId);

      return {
        graph,
        dag: buildDag(graph),
        graphVersion: s.getGraphVersion(graphId),
        topologyVersion: s.getTopologyVersion(graphId),
      };
    },
    getGraphVersion: (graphId) => {
      return graphStore().getGraphVersion(graphId);
    },
    getGraphTemplate: (graphId) => {
      return graphStore().getGraphTemplate(graphId);
    },
    getNodeEvaluation: (graphId, nodeId) => {
      return graphStore().getNodeEvaluation(graphId, nodeId);
    },
    setNodeTemplate: (graphId, nodeId, template) => {
      graphStore().setNodeTemplate(graphId, nodeId, template);
    },
    markNodesRunning: (graphId, nodeIds) => {
      const s = graphStore();

      nodeIds.forEach((nodeId) => {
        s.setNodeEvaluation(graphId, nodeId, {
          nodeId,
          status: NodeEvaluationStatus.Running,
          valueRef: { kind: EvaluationValueKind.Empty },
          error: null,
          fingerprint: 0,
        });
      });
    },
    applyNodeSuccess: (graphId, result) => {
      const s = graphStore();

      s.setNodeEvaluation(graphId, result.nodeId, {
        nodeId: result.nodeId,
        status: NodeEvaluationStatus.Ok,
        valueRef: toEvaluationValue(result.result),
        error: null,
        fingerprint: 0,
      });
    },
    applyNodeError: (graphId, nodeId, message) => {
      const s = graphStore();

      s.setNodeEvaluation(graphId, nodeId, {
        nodeId,
        status: NodeEvaluationStatus.Error,
        valueRef: { kind: EvaluationValueKind.Empty },
        error: {
          code: EvaluationErrorType.OTHER,
          message,
        },
        fingerprint: 0,
      });
    },
    applyNodesSkipped: (graphId, nodeIds) => {
      const s = graphStore();
      const e = entryStore();

      nodeIds.forEach((nodeId) => {
        s.setNodeEvaluation(graphId, nodeId, {
          nodeId,
          status: NodeEvaluationStatus.Idle,
          valueRef: { kind: EvaluationValueKind.Empty },
          error: null,
          fingerprint: 0,
        });
      });

      if (nodeIds.includes(TERMINAL_NODE_ID)) {
        match(s.get(graphId).ref)
          .with({ kind: GraphRefType.Field }, (fieldRef) => {
            e.setComputed(fieldRef.fieldId, null);
          })
          .otherwise(() => void 0);
      }
    },
    applyTerminalResult: (batch, result) => {
      if (result.nodeId !== TERMINAL_NODE_ID) return;

      const s = graphStore();
      const e = entryStore();

      match(s.get(batch.graphId).ref)
        .with({ kind: GraphRefType.Field }, (fieldRef) => {
          if (result.status === NodeEvalStatus.Ok) {
            e.setComputed(fieldRef.fieldId, result.result);
          } else {
            e.setComputed(fieldRef.fieldId, null);
          }
        })
        .with({ kind: GraphRefType.Param }, (paramRef) => {
          const parentGraphId = paramRef.graphId;
          s.enqueueGraph(parentGraphId, [paramRef.ownerNodeId]);
        })
        .exhaustive();
    },
  };
};
